import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { Theme, FilterDialog, ReportStep } from '@/types';

// Single key for all persisted admin preferences. Privacy disclosure says
// "we store one localStorage entry: logspot-settings". Components read/write
// individual fields via the slot atoms below; nothing should ever talk to
// localStorage directly.
type Preferences = {
  theme: Theme;
  projectsByOrg: Record<string, string>;
  trialBannerDismissed: boolean;
  trialEndingSeen: boolean;
};

const DEFAULT_PREFERENCES: Preferences = {
  theme: 'dark',
  projectsByOrg: {},
  trialBannerDismissed: false,
  trialEndingSeen: false,
};

// One-time migration from the previous per-feature keys into the consolidated
// `logspot-settings` key. Runs at module load. The merge step is skipped once
// the consolidated key exists; the sweep is always safe (idempotent).
if (typeof window !== 'undefined') {
  try {
    const ls = window.localStorage;
    const has = (k: string) => ls.getItem(k) !== null;
    if (
      !has('logspot-settings') &&
      (has('ls-theme') ||
        has('ls-project-id-by-org') ||
        has('ls-demo') ||
        has('logspot_trial_banner_dismissed'))
    ) {
      const next: Preferences = { ...DEFAULT_PREFERENCES };
      const themeRaw = ls.getItem('ls-theme');
      if (themeRaw) {
        try {
          const parsed = JSON.parse(themeRaw);
          if (parsed === 'dark' || parsed === 'light' || parsed === 'system') {
            next.theme = parsed;
          }
        } catch {
          // legacy value was invalid JSON — fall back to default
        }
      }
      const mapRaw = ls.getItem('ls-project-id-by-org');
      if (mapRaw) {
        try {
          const parsed = JSON.parse(mapRaw);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            next.projectsByOrg = parsed as Record<string, string>;
          }
        } catch {
          // legacy value was invalid JSON — fall back to empty map
        }
      }
      next.trialBannerDismissed =
        ls.getItem('logspot_trial_banner_dismissed') === 'true';
      ls.setItem('logspot-settings', JSON.stringify(next));
    }
    // Always sweep — idempotent if the key is already absent.
    [
      'ls-theme',
      'ls-project-id-by-org',
      'ls-demo',
      'logspot_trial_banner_dismissed',
      'ls-project-id',
      'ls-token',
    ].forEach((k) => ls.removeItem(k));
  } catch {
    // localStorage unavailable (private mode quota, disabled, etc.) — defaults
    // will apply and persistence is a no-op for this session.
  }
}

// Single source of truth for all persisted admin preferences.
const preferencesAtom = atomWithStorage<Preferences>(
  'logspot-settings',
  DEFAULT_PREFERENCES,
  undefined,
  { getOnInit: true },
);

// Writable derived atom that reads/writes a single slot of preferencesAtom.
// Same shape as a regular Jotai atom (value or updater fn).
function preferenceSlotAtom<K extends keyof Preferences>(key: K) {
  return atom(
    (get) => get(preferencesAtom)[key],
    (
      get,
      set,
      update: Preferences[K] | ((prev: Preferences[K]) => Preferences[K]),
    ) => {
      const current = get(preferencesAtom);
      const next =
        typeof update === 'function'
          ? (update as (prev: Preferences[K]) => Preferences[K])(current[key])
          : update;
      if (current[key] !== next) {
        set(preferencesAtom, { ...current, [key]: next });
      }
    },
  );
}

export const themeAtom = preferenceSlotAtom('theme');
export const trialBannerDismissedAtom = preferenceSlotAtom(
  'trialBannerDismissed',
);

// The trial-ending reminder shows once per browser.
export const trialEndingSeenAtom = preferenceSlotAtom('trialEndingSeen');

// Lean onboarding: the Manage Plan sheet opens from the trial banner, Getting Started and credit actions.
type PlanSheetState = { open: boolean; source: string; withPack: boolean };
export const planSheetAtom = atom<PlanSheetState>({
  open: false,
  source: '',
  withPack: false,
});
export const launchChecklistOpenAtom = atom(false);

// The active organization id is mirrored into Jotai by _auth.beforeLoad and
// setActiveOrganization so derived projectIdAtom can read the right slot from
// outside React (route loaders) and inside React (components) consistently.
export const activeOrgIdAtom = atom<string | null>(null);

// projectIdAtom — derives from preferencesAtom.projectsByOrg + activeOrgIdAtom.
// Same public shape as a primitive atom (string | null + updater fn) so
// existing useAtom(projectIdAtom) consumers stay unchanged.
export const projectIdAtom = atom(
  (get) => {
    const orgId = get(activeOrgIdAtom);
    return orgId ? (get(preferencesAtom).projectsByOrg[orgId] ?? null) : null;
  },
  (
    get,
    set,
    update: string | null | ((prev: string | null) => string | null),
  ) => {
    const orgId = get(activeOrgIdAtom);
    if (!orgId) return;
    const prefs = get(preferencesAtom);
    const map = prefs.projectsByOrg;
    const prev = map[orgId] ?? null;
    const next = typeof update === 'function' ? update(prev) : update;
    if (next === prev) return;
    let nextMap: Record<string, string>;
    if (next === null) {
      const { [orgId]: _, ...rest } = map;
      nextMap = rest;
    } else {
      nextMap = { ...map, [orgId]: next };
    }
    set(preferencesAtom, { ...prefs, projectsByOrg: nextMap });
  },
);

export const stepsAtom = atom<ReportStep[]>([]);

const defaultFilterDialog = {
  search: '',
  searchValue: '',
  selectedField: null,
  selectedOperator: null,
  selectedValue: null,
  currentTab: 'fields',
};

export const filterDialogAtom = atom<FilterDialog>(defaultFilterDialog);

export const serviceUnavailableAtom = atom(false);
